import { DataTable, SelectInput, SkeletonLoader, Pagination, BasicModal, Button, TextInput, AlertBox, PhoneInput } from '@life-line-lab/components-react';
import { DEFAULT_MAX_PER_PAGE } from 'Components/shared/constants/config';
import { useItemsPerPage } from 'Components/shared/hooks';
import { decode } from 'Helpers/enc';
import useQuery from 'Hooks/query.hook';
import useSession from 'Hooks/session.hook';
import { first, flatMap, flatten, omitBy, isEmpty, isNil } from 'lodash';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory, useParams } from 'react-router-dom';
import { assignVouchers, fetchOrder, fetchOrderById } from 'Services/order.services';
import moment from 'moment';
import Icon, { ArrowRightOutlined, EditOutlined, EyeOutlined, LeftOutlined, MailOutlined, PlusOutlined, RightOutlined } from '@ant-design/icons';
import './telemed-voucher-list.scss';
import { PurchasedVoucher, PurchasedVoucherAssignedTo } from 'src/models/telemed-voucher';
import { toaster } from 'Helpers/toaster';
import VoucherAssignModal from 'Components/order/purchase-list/voucher-assign-modal';

const TelemedVoucherListPage = () => {
  const query = useQuery();
  const history = useHistory();
  const { t } = useTranslation();
  const { id } = useParams<{ id: string }>();
  const orderId = decode(id);

  const [orderData, setOrderData] = useState<any>(undefined);
  const totalVoucherCount = !orderData ? 0 :
    flatten(orderData.items.map(item => item.vouchers)).length;
  const [itemPerPage, setItemPerPage] = useItemsPerPage(DEFAULT_MAX_PER_PAGE.toString());

  const page: any = query.get('page') || 1;
  const initialQueryParams = { page, offset: (page - 1) * DEFAULT_MAX_PER_PAGE };
  const [queryParams, setQueryParams] = useState(initialQueryParams);

  const [selectedProductIndex, setSelectedProductIndex] = useState<any>(0); // default selected first product
  const [selectedVouchersList, setSelectedVouchersList] = useState<any[]>([]);  // checkboxes
  const [vouchersToAssign, setVouchersToAssign] = useState<PurchasedVoucher[]>([]);
  const [showVoucherAssignModal, setShowVoucherAssignModal] = useState(false);

  useEffect(() => {
    const session = useSession();
    if (isEmpty(session)) {
      history.push('/login');
      return;
    }
  }, []);

  useEffect(() => {
    if (!orderId) return;

    fetchOrderData();
  }, [orderId]);

  const getCurrentVoucherList = (): any[] => {
    if (!orderData) return [];
    if (isEmpty(orderData.items)) return [];

    const filteredItems = isNil(selectedProductIndex) ? 
                            orderData.items :
                            [orderData.items[selectedProductIndex]];
    const filteredVouchers = flatten(filteredItems.map(item => {
      const foundVariant = item.product?.product?.productVariants?.find(pv => pv.variantCode === item.variantCode);

      const productName = foundVariant ? 
        (item.product?.product?.name + ' - ' + foundVariant.variantName) :
        item.product?.product?.name;

      if (isEmpty(item.vouchers)) {
        return [];
      }

      return item.vouchers?.map(voucher => ({
        ...voucher,
        productName,
      }))
    }));

    // pagination
    const results = filteredVouchers.slice(queryParams.offset, queryParams.offset + itemPerPage);
    return results;
  }

  const showModalForVoucher = (voucherRecord: PurchasedVoucher) => {
    setVouchersToAssign([voucherRecord]);
    setShowVoucherAssignModal(true);
  }

  const fetchOrderData = async () => {
    const responseData = await fetchOrder(orderId + '');

    if (!isNil(responseData)) {
      setOrderData(responseData)
    }
  };

  const handleMultiAssignTo = () => {
    if (isEmpty(selectedVouchersList)) {
      toaster('info', t('txt.noVouchersSelectedToAssign'));
      return;
    }
    setVouchersToAssign(selectedVouchersList);
    setShowVoucherAssignModal(true);
  }

  const rowSelection = {
    selectedRowKeys: selectedVouchersList.map(v => v.code),
    onChange: (selectedRowKeys, selectedRows) => {
      setSelectedVouchersList(selectedRows);
    },
    // onSelectAll: (selected, selectedRowKeys: any, selectedRows: any) => {
    //   setSelectedVouchersList(selectedRowKeys)
    // },
    
    // onSelect: (record, selected, selectedRows: any) => {
    //   setSelectedVouchersList([selected])
    // },
    getCheckboxProps: (record: any) => {
      return {
        disabled: !isEmpty(record.assignedTo),
      }
    }
  };

  const handleCopyToClipboard = (str: string) => {
    const el = document.createElement('textarea');
    el.value = str;
    el.setAttribute('readonly', '');
    el.style.position = 'absolute';
    el.style.left = '-9999px';
    document.body.appendChild(el);
    el.select();
    document.execCommand('copy');
    document.body.removeChild(el);

    toaster('success', t('toaster.copiedToClipboard'));
  };

  const columns = [
    {
      title: t('label.productName'),
      dataIndex: ['productName'],
      render: (_: any, record: any) => {
        return (
          <div>
            { record.productName }
          </div>
        )
      }
    },
    {
      title: t('label.voucherCode'),
      dataIndex: ['voucherCode'],
      render: (_: any, record: any) => {
        return (
          <div>
            { record.code }
          </div>
        )
      }
    },
    {
      title: t('label.expiresAt'),
      dataIndex: ['expiresAt'],
      render: (_: any, record: any) => {
        return (
          <div>
            { record.expiresAt ? moment(record.expiresAt).format('YYYY-MM-DD HH:mm:ss') : '-' }
          </div>
        )
      }
    },
    {
      title: t('label.cancellationExpiryDate'),
      dataIndex: ['cancellationExpiryDate'],
      render: (_: any, record: any) => {
        return (
          <div>
            { record.cancellationExpiryDate ? moment(record.cancellationExpiryDate).format('YYYY-MM-DD HH:mm:ss') : '-' }
          </div>
        )
      }
    },
    {
      title: t('label.voucherStatus'),
      dataIndex: ['voucherStatus'],
      render: (_: any, record: any) => {
        return (
          <div>
            { record.status }
          </div>
        )
      }
    },
    {
      title: t('label.assignedTo'),
      dataIndex: ['assignedTo'],
      render: (_: any, record: any) => {
        if (!isEmpty(record.assignedTo)) {
          return (
            <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'flex-start', flexDirection: 'column', gap: 8 }} >
              {record.assignedTo.fullName && 
                <div>
                  <span>
                    {record.assignedTo.fullName}
                  </span>
                </div>
              }
              {(record.assignedTo.contactNumber) &&
              <div className="meta-data">
                <span>
                  <Icon component={() => (
                    <svg viewBox="0 0 1024 1024" version="1.1" xmlns="http://www.w3.org/2000/svg" width="15px" height="15px" p-id="20109">
                      <path d="M878.52 454.35c-9.48-195.74-175.85-346.73-371.59-337.25-182.47 8.84-328.4 154.78-337.25 337.25a159.918 159.918 0 0 0-50.49 116.43c0 60.05 35.64 131.36 91.65 131.36 50.49 0 50.49-56.29 50.49-131.36 0-69.43 0-125.72-33.67-131.38 17.06-163.32 163.3-281.89 326.62-264.83 138.36 14.45 248.2 122.91 264.41 261.07-33.67 11.26-33.67 61.92-33.67 131.36a348.65 348.65 0 0 0 9.36 110.73A316.037 316.037 0 0 1 601.74 820.3a99.248 99.248 0 0 0-80.43-35.64c-50.49 0-91.65 26.28-91.65 56.29s41.16 56.29 91.65 56.29 91.65-26.28 91.65-56.29a342.73 342.73 0 0 0 196.39-146.37c5.61 5.63 14.97 5.63 26.14 5.63 56.1 0 91.65-71.29 91.65-131.36a150.407 150.407 0 0 0-48.62-114.5z" p-id="20110"></path>
                      <path d="M486.17 547.1c18.2 0.01 32.97-14.73 32.98-32.94v-81.62c0.01-18.2-14.73-32.97-32.94-32.98h-0.04c-17.96 0.6-32.38 15.02-32.98 32.98v81.58c-0.01 18.2 14.73 32.97 32.94 32.98h0.04z m187.46-34.72V430.8c0.01-18.2-14.73-32.97-32.94-32.98h-0.04c-17.96 0.61-32.37 15.02-32.98 32.98v81.69c-0.05 18.21 14.68 33.02 32.89 33.07 18.21 0.05 33.02-14.68 33.07-32.89v-0.29z" p-id="20111"></path>
                    </svg>
                  )} /> &nbsp;
                  <span>
                    {`+${record.assignedTo?.contactNumber?.fullNumber}`}
                  </span>
                </span>
              </div>
              }

              {(record.assignedTo?.email) &&
              <div className="meta-data">
                <span>
                  <MailOutlined /> &nbsp;
                  <span>
                    {record.assignedTo?.email}
                  </span>
                </span>
              </div>
              }
            </div>
          )
        }
        return (
          <a style={{ display: 'flex', justifyContent: 'flex-start', alignItems: 'center'}} 
            onClick={() => showModalForVoucher(record)}>
            {/* <EditOutlined style={{ fontSize: 16 }} /> */}
            {t('action.assign')}
          </a>
        )
      }
    },
  ]

  const currentVoucherList = isEmpty(orderData) ? 
                              [] :
                              getCurrentVoucherList();

  const DataTableHeaderComponent = () => {
    if (!orderData) {
      return <></>;
    }

    return (
      <div className='px-10 my-20'>
        <div style={{ display: 'flex', justifyContent: 'space-between', gap: 24, }}>
          <div>
            <SelectInput
              label={t("label.product")}
              value={selectedProductIndex as string}
              onChange={
                (prodIndex) => setSelectedProductIndex(prodIndex)
              }
              allowClear={true}
              options={orderData.items.filter(oid => !isEmpty(oid.vouchers)).map((v, i) => {
                const foundVariant = v.product?.product?.productVariants?.find(pv => pv.variantCode === v.variantCode);

                const label = foundVariant ? 
                  (v.product?.product?.name + ' - ' + foundVariant.variantName) :
                  v.product?.product?.name;

                return {
                  label: label,
                  value: i,
                }
              })}
              style={{width: '300px'}}
            />
          </div>
          <div>
            <Button
              type="primary"
              label={
                <React.Fragment>
                  { t('action.assignTo') }
                </React.Fragment>
              }
              size="large"
              onClick={handleMultiAssignTo}
            />
          </div>
        </div>
      </div>
    )
  }

  const DataTableComponent = () => {
    if (!orderData) {
      return (
        <>
          <SkeletonLoader type="table" />
        </>
      )
    }

    return (
      <DataTable
        rowSelection={{
          type: 'checkbox',
          ...rowSelection,
        }}
        rowKey={(record: any) => {
          return record.code;
        }}
        rowClassName={(record, index) => {
          return (
            !isEmpty(record.assignedTo) ?
            'table-row-voucher-assigned' :
            ''
          )
        }}
        // onRow={(record, rowIndex) => {
        //   if (isEmpty(record.assignedTo)) {
        //     return undefined;
        //   }

        //   return {
        //     onClick: () => showModalForVoucher(record),
        //   }
        // }}
        columns={columns}
        dataSource={currentVoucherList}
      />
    )
  }

  const handleItemsPerPageChange = (value) => {
    setItemPerPage(value)
  }

  const handlePaginationChange = (value) => {
    let page = queryParams.page;
    let limit: number = itemPerPage;

    if (value === 'NEXT') {
      const endOfList = page * itemPerPage === totalVoucherCount;
      if (currentVoucherList.length == itemPerPage && !endOfList) ++page;
      if (page > itemPerPage) page = itemPerPage;
    }

    if (value === 'PREVIOUS') {
      --page;
      if (page < 1) page = 1;
    }

    const offset = (page - 1) * limit;
    setQueryParams({ ...queryParams, page, offset });
  }

  const PaginationComponent = () => {

    return (
      <Pagination
        previousIcon={<LeftOutlined />}
        nextIcon={<RightOutlined />}
        itemsPerPageValue={itemPerPage as number}
        itemsPerPageConfig={{
          initialValue: DEFAULT_MAX_PER_PAGE,
          prefixLabel: t('label.show'),
          suffixLabel: t('txt.records')
        }}
        onItemsPerPageChange={handleItemsPerPageChange}
        onChange={handlePaginationChange}
      />
    )
  }

  const onCloseModal = () => {
    setShowVoucherAssignModal(false);
    setVouchersToAssign([]);
  }

  const onSubmit = async (value) => {
    if (isEmpty(vouchersToAssign)) return;

    try {
      const response = await assignVouchers({
        orderID: orderData._id,
        assignedTo: value,
        vouchers: vouchersToAssign,
      })
      if (isEmpty(response.error)) {
        toaster('success', t('toaster.assignVouchersSuccess'));
        await fetchOrderData();
        onCloseModal();
        setSelectedVouchersList([]);
      } else {
        toaster('error', t('toaster.assignVouchersFailed'));
      }
    } catch (err) {
      toaster('error', t('toaster.assignVouchersFailed'));
    }
  }

  return (
    <div className='telemed-voucher-list'>
      <div className="page" style={{ marginLeft: 'auto', marginRight: 'auto' }}>
        <div style={{ position: 'relative', background: '#ffffff', borderRadius: '4px', padding: '20px' }}>

          <div style={{ display: 'flex', flexDirection: 'column', gap: '10px', marginBottom: '15px' }}>
            <h3 className="title" style={{ color: '#CF4392', fontSize: '24px' }}>{t('title.viewVoucherList')}</h3>
            {
              orderData ?
              <h4 className="ml-10 color-primary">ORDER #{orderData.orderCode}</h4> :
              null
            }
          </div>
          <DataTableHeaderComponent />
          <DataTableComponent />
          <PaginationComponent />
        </div>

        <VoucherAssignModal 
          onCloseModal={onCloseModal}
          onSubmit={onSubmit}
          showVoucherAssignModal={showVoucherAssignModal}
          vouchersToAssign={vouchersToAssign}
        />
      </div>
    </div>
  )
}

export default TelemedVoucherListPage;
